import { ChevronDown, ChevronLeft, ChevronRight, Maximize2, RefreshCw } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOverlay } from '../../contexts/OverlayContext';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import loggingService from '../../services/loggingService';
import { typography } from '../../styles/typography';
import Calendar from '../Schedule/calendar';
import { getAllSites } from '../SiteManagement/siteApi';
import PrimaryButton from '../ui/PrimaryButton';
import { facilityBookingApi } from './facilityBookingApi';

const FacilityBooking = () => {
  // Log screen view
  useScreenLogging('FacilityBooking', {
    component: 'FacilityBooking',
    screenType: 'amenity-booking',
  });

  const navigate = useNavigate();
  const location = useLocation();
  const { openOverlay, closeOverlay } = useOverlay();

  // Helper function to get today's date
  const getToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  const today = getToday();

  // Helper to get today's date formatted for <input type="date" />
  const getTodayInputValue = () => {
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper to get ISO week number
  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  // State for calendar
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(today);
  const [isMonthYearPickerOpen, setIsMonthYearPickerOpen] = useState(false);
  const [pickerYear, setPickerYear] = useState(new Date().getFullYear());
  const [pickerMonth, setPickerMonth] = useState(new Date().getMonth());
  const [pickerWeek, setPickerWeek] = useState(getWeekNumber(today)); // Week number in year (1-53)
  const [pickerDay, setPickerDay] = useState(new Date().getDate());
  const [currentView, setCurrentView] = useState('timeGridWeek'); // Track current calendar view
  const calendarRef = useRef(null);
  const monthYearPickerRef = useRef(null);

  // Responsive layout state
  const [isMobileViewport, setIsMobileViewport] = useState(false);

  // State for facility type dropdown
  const [isFacilityDropdownOpen, setIsFacilityDropdownOpen] = useState(false);
  const facilityDropdownRef = useRef(null);

  // Booking form state
  const [selectedFacilityType, setSelectedFacilityType] = useState('');
  const [otherFacilityType, setOtherFacilityType] = useState('');
  const [purposeOfBooking, setPurposeOfBooking] = useState('');
  // For multiple date selection - list of dates and current picker value
  const [bookingDates, setBookingDates] = useState([getTodayInputValue()]);
  const [bookingDateInput, setBookingDateInput] = useState(getTodayInputValue());
  const [bookingStartTime, setBookingStartTime] = useState('09:00');
  const [bookingEndTime, setBookingEndTime] = useState('10:00');
  const [timeError, setTimeError] = useState('');
  const [residentName, setResidentName] = useState('');
  const [whosBooking, setWhosBooking] = useState('');
  const [blockNumber, setBlockNumber] = useState('');
  const [apartmentFlatNo, setApartmentFlatNo] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [email, setEmail] = useState('');
  const [numberOfPeople, setNumberOfPeople] = useState('');
  const [bookingNotes, setBookingNotes] = useState('');
  
  // Site selection state
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [isSitesLoading, setIsSitesLoading] = useState(false);
  const [isSiteDropdownOpen, setIsSiteDropdownOpen] = useState(false);
  const siteDropdownRef = useRef(null);

  // Amenities state
  const [amenities, setAmenities] = useState([]);
  const [selectedAmenity, setSelectedAmenity] = useState('');
  const [isAmenitiesLoading, setIsAmenitiesLoading] = useState(false);
  const [isAmenityDropdownOpen, setIsAmenityDropdownOpen] = useState(false);
  const amenityDropdownRef = useRef(null);

  // Helper function to convert time string to minutes for comparison
  const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Validate time selection
  const validateTime = (startTime, endTime) => {
    const startMinutes = timeToMinutes(startTime);
    const endMinutes = timeToMinutes(endTime);
    
    // Check if times are the same
    if (startMinutes === endMinutes) {
      return 'Start and end time cannot be the same';
    }
    
    // Start time must be before end time (no overnight bookings)
    if (startMinutes >= endMinutes) {
      return 'Start time must be before end time';
    }
    
    // Minimum booking duration of 30 minutes
    const duration = endMinutes - startMinutes;
    if (duration > 0 && duration < 30) {
      return 'Minimum booking duration is 30 minutes';
    }
    
    return ''; // Valid
  };

  // Handle start time change with validation
  const handleStartTimeChange = (newStartTime) => {
    setBookingStartTime(newStartTime);
    const error = validateTime(newStartTime, bookingEndTime);
    setTimeError(error);
  };

  // Handle end time change with validation
  const handleEndTimeChange = (newEndTime) => {
    setBookingEndTime(newEndTime);
    const error = validateTime(bookingStartTime, newEndTime);
    setTimeError(error);
  };

  // Bookings state
  const [bookings, setBookings] = useState([]);
  const [facilityInvoices, setFacilityInvoices] = useState([]);
  const [localBookings, setLocalBookings] = useState(() => {
    // Load locally cached bookings from localStorage
    try {
      const cached = localStorage.getItem('facility_bookings_local');
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Save local bookings to localStorage
  const saveLocalBooking = (booking) => {
    const updated = [...localBookings, booking];
    setLocalBookings(updated);
    try {
      localStorage.setItem('facility_bookings_local', JSON.stringify(updated));
    } catch (e) {
      console.warn('Failed to save local booking:', e);
    }
  };

  // Clear local bookings when API returns data
  const clearLocalBookingsIfSynced = (apiBookings) => {
    if (apiBookings && apiBookings.length > 0) {
      // API is working, clear local cache
      setLocalBookings([]);
      try {
        localStorage.removeItem('facility_bookings_local');
      } catch (e) {
        console.warn('Failed to clear local bookings:', e);
      }
    }
  };

  // Facility type options
  const facilityTypes = [
    'Mini Theater',
    'Billiards',
    'Hall',
    'Badminton Court',
    'Other',
  ];

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        facilityDropdownRef.current &&
        !facilityDropdownRef.current.contains(event.target)
      ) {
        setIsFacilityDropdownOpen(false);
      }
      if (
        siteDropdownRef.current &&
        !siteDropdownRef.current.contains(event.target)
      ) {
        setIsSiteDropdownOpen(false);
      }
      if (
        amenityDropdownRef.current &&
        !amenityDropdownRef.current.contains(event.target)
      ) {
        setIsAmenityDropdownOpen(false);
      }
      if (
        monthYearPickerRef.current &&
        !monthYearPickerRef.current.contains(event.target)
      ) {
        setIsMonthYearPickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch sites on component mount
  useEffect(() => {
    const fetchSites = async () => {
      setIsSitesLoading(true);
      try {
        const result = await getAllSites();
        console.log('getAllSites result:', result);
        
        // Handle different response formats
        let sitesArray = [];
        if (result.success && result.sites) {
          sitesArray = result.sites;
        } else if (Array.isArray(result)) {
          sitesArray = result;
        } else if (result.data && Array.isArray(result.data)) {
          sitesArray = result.data;
        }
        
        console.log('Sites array:', sitesArray);
        setSites(sitesArray);
        
        // Log successful API call
        loggingService.logInfo(
          'amenityFetchSites',
          'Successfully fetched sites for amenity booking',
          {
            endpoint: '/sites',
            method: 'GET',
            siteCount: sitesArray.length,
          }
        ).catch(logError => {
          console.warn('[FacilityBooking] Failed to log event:', logError);
        });
        
        // Don't auto-select - let user select from dropdown
      } catch (error) {
        console.error('Error fetching sites:', error);
      } finally {
        setIsSitesLoading(false);
      }
    };
    fetchSites();
  }, []);

  // Fetch amenities when site changes
  useEffect(() => {
    const fetchAmenities = async () => {
      if (!selectedSite) {
        setAmenities([]);
        setSelectedAmenity('');
        return;
      }
      
      setIsAmenitiesLoading(true);
      try {
        const amenitiesData = await facilityBookingApi.getAmenities(selectedSite);
        setAmenities(amenitiesData || []);
        
        // Log successful API call
        loggingService.logInfo(
          'amenityFetchAmenities',
          'Successfully fetched amenities',
          {
            endpoint: '/facility/amenities',
            method: 'GET',
            siteId: selectedSite,
            amenityCount: amenitiesData?.length || 0,
          }
        ).catch(logError => {
          console.warn('[FacilityBooking] Failed to log event:', logError);
        });
        
        // Reset selected amenity when site changes
        setSelectedAmenity('');
      } catch (error) {
        console.error('Error fetching amenities:', error);
        setAmenities([]);
        // Error logging is handled in facilityBookingApi.js
      } finally {
        setIsAmenitiesLoading(false);
      }
    };
    fetchAmenities();
  }, [selectedSite]);

  // Get amenity options for dropdown
  const amenityOptions = useMemo(() => {
    return amenities.map(amenity => ({
      value: amenity.amenity_id || amenity.id,
      label: amenity.amenity_name || amenity.name || `Amenity ${amenity.amenity_id || amenity.id}`,
      type: amenity.amenity_type,
      capacity: amenity.capacity,
      hourlyRate: amenity.hourly_rate,
      isPaid: amenity.is_paid,
    })).filter(opt => opt.value);
  }, [amenities]);

  // Get selected amenity details
  const getSelectedAmenityDetails = () => {
    return amenityOptions.find(a => a.value === selectedAmenity);
  };

  // Get selected amenity label
  const getSelectedAmenityLabel = () => {
    const amenity = getSelectedAmenityDetails();
    return amenity?.label || 'Select Amenity';
  };

  // Get site options for dropdown (sorted alphabetically)
  const siteOptions = useMemo(() => {
    return [...sites]
      .sort((a, b) => {
        const nameA = (a.site_name || a.name || 'Unknown Site').toLowerCase();
        const nameB = (b.site_name || b.name || 'Unknown Site').toLowerCase();
        return nameA.localeCompare(nameB);
      })
      .map(site => ({
        value: site.site_id || site.id,
        label: site.site_name || site.name || `Site ${site.site_id || site.id}`,
      }))
      .filter(opt => opt.value);
  }, [sites]);

  // Get selected site label
  const getSelectedSiteLabel = () => {
    const site = siteOptions.find(s => s.value === selectedSite);
    return site?.label || 'Select a site';
  };

  // Handle responsive layout on mobile screens
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const isMobile = width < 768;
      setIsMobileViewport(isMobile);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Fetch bookings and invoices for calendar display
  const fetchBookings = useCallback(async () => {
    setIsLoading(true);
    try {
      const datePrefix = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`;
      
      // Fetch bookings and invoices separately to handle individual failures
      let bookingsData = [];
      let invoicesData = [];
      
      // Try fetching bookings (may fail with 502 if backend not ready)
      try {
        bookingsData = await facilityBookingApi.getBookings({ siteId: selectedSite || undefined });
        // If API returns data, clear local cache
        if (bookingsData && bookingsData.length > 0) {
          clearLocalBookingsIfSynced(bookingsData);
        }
        
        // Log successful API call
        loggingService.logInfo(
          'amenityFetchBookings',
          'Successfully fetched facility bookings',
          {
            endpoint: '/facility/bookings',
            method: 'GET',
            siteId: selectedSite || undefined,
            bookingCount: bookingsData?.length || 0,
            month: datePrefix,
          }
        ).catch(logError => {
          console.warn('[FacilityBooking] Failed to log event:', logError);
        });
      } catch (bookingsError) {
        console.warn('Bookings API not available:', bookingsError.message);
        // Continue without bookings - this is expected if backend isn't ready
        // Error logging is handled in facilityBookingApi.js
      }
      
      // Try fetching invoices
      try {
        invoicesData = await facilityBookingApi.getFacilityInvoices({ dateFrom: datePrefix });
        
        // Log successful API call
        loggingService.logInfo(
          'amenityFetchInvoices',
          'Successfully fetched facility invoices',
          {
            endpoint: '/facility/invoices',
            method: 'GET',
            siteId: selectedSite || undefined,
            invoiceCount: invoicesData?.length || 0,
            month: datePrefix,
          }
        ).catch(logError => {
          console.warn('[FacilityBooking] Failed to log event:', logError);
        });
      } catch (invoicesError) {
        console.warn('Invoices API not available:', invoicesError.message);
        // Error logging is handled in facilityBookingApi.js
      }
      
      setBookings(bookingsData || []);
      setFacilityInvoices(invoicesData || []);
    } catch (error) {
      console.error('Error fetching facility data:', error);
      setBookings([]);
      setFacilityInvoices([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentMonth, selectedSite]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  // Refresh bookings when returning from invoice creation
  useEffect(() => {
    if (location.state?.refresh || location.state?.invoiceCreated) {
      console.log('Refreshing bookings after invoice creation');
      fetchBookings();
      // Clear the state to prevent re-fetching on subsequent renders
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, fetchBookings, navigate, location.pathname]);

  // Get calendar days for the month
  const getCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);

    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      const isCurrentMonth = date.getMonth() === month;
      const isToday =
        date.toDateString() === todayDate.toDateString() &&
        date.getMonth() === todayDate.getMonth() &&
        date.getFullYear() === todayDate.getFullYear();
      const isSelected =
        selectedDate && date.toDateString() === selectedDate.toDateString();

      days.push({
        date,
        isCurrentMonth,
        isToday,
        isSelected,
      });
    }

    return days;
  };

  // Navigate months
  const navigateMonth = direction => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };

  // Handle calendar title click to open appropriate picker based on view
  const handleTitleClick = (date, viewType) => {
    setCurrentView(viewType);
    const currentDate = new Date(date);
    
    if (viewType === 'dayGridMonth') {
      // Month view - show month/year picker
      setPickerYear(currentDate.getFullYear());
      setPickerMonth(currentDate.getMonth());
    } else if (viewType === 'timeGridWeek') {
      // Week view - show week/year picker
      setPickerYear(currentDate.getFullYear());
      // Calculate week number (ISO week, 1-53)
      const weekNumber = getWeekNumber(currentDate);
      setPickerWeek(weekNumber);
    } else if (viewType === 'timeGridDay') {
      // Day view - show day/month/year picker
      setPickerYear(currentDate.getFullYear());
      setPickerMonth(currentDate.getMonth());
      setPickerDay(currentDate.getDate());
    }
    
    setIsMonthYearPickerOpen(true);
  };

  // Get date from week number and year
  const getDateFromWeek = (weekNumber, year) => {
    const simple = new Date(year, 0, 1 + (weekNumber - 1) * 7);
    const dow = simple.getDay();
    const ISOweekStart = simple;
    if (dow <= 4) {
      ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
    } else {
      ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
    }
    return ISOweekStart;
  };

  // Generate list of weeks for a year
  const getWeeksForYear = (year) => {
    const weeks = [];
    // Most years have 52 weeks, some have 53
    const maxWeeks = getWeekNumber(new Date(year, 11, 31)) === 1 ? 52 : 53;
    for (let i = 1; i <= maxWeeks; i++) {
      const weekStart = getDateFromWeek(i, year);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      weeks.push({
        number: i,
        start: weekStart,
        end: weekEnd,
        label: `Week ${i} (${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`
      });
    }
    return weeks;
  };

  // Generate list of days for a month
  const getDaysForMonth = (month, year) => {
    const days = [];
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      days.push({
        day: i,
        date: date,
        label: `${i} ${date.toLocaleDateString('en-US', { weekday: 'short' })}`
      });
    }
    return days;
  };

  // Handle selection from picker based on current view
  const handlePickerSelect = () => {
    let newDate;
    
    if (currentView === 'dayGridMonth') {
      // Month view - navigate to first day of selected month
      newDate = new Date(pickerYear, pickerMonth, 1);
    } else if (currentView === 'timeGridWeek') {
      // Week view - navigate to start of selected week
      newDate = getDateFromWeek(pickerWeek, pickerYear);
    } else if (currentView === 'timeGridDay') {
      // Day view - navigate to selected day
      newDate = new Date(pickerYear, pickerMonth, pickerDay);
    } else {
      // Fallback to month
      newDate = new Date(pickerYear, pickerMonth, 1);
    }
    
    setCurrentMonth(newDate);
    setSelectedDate(newDate);
    
    // Navigate calendar to selected date (use changeView for month so the selected month is shown)
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      if (calendarApi) {
        if (currentView === 'dayGridMonth') {
          calendarApi.changeView('dayGridMonth', newDate);
        } else if (currentView === 'timeGridWeek') {
          calendarApi.changeView('timeGridWeek', newDate);
        } else if (currentView === 'timeGridDay') {
          calendarApi.changeView('timeGridDay', newDate);
        } else {
          calendarApi.gotoDate(newDate);
        }
      }
    }
    
    setIsMonthYearPickerOpen(false);
  };

  // Handle calendar dates change to sync currentMonth state and track view
  const handleDatesSet = (dateInfo) => {
    if (dateInfo && dateInfo.start) {
      const viewDate = new Date(dateInfo.start);
      setCurrentMonth(viewDate);
      
      // Track current view type
      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();
        if (calendarApi && calendarApi.view) {
          setCurrentView(calendarApi.view.type);
        }
      }
    }
  };

  // Handle opening full-screen calendar overlay
  const handleViewCalendarFullScreen = () => {
    openOverlay('facilityBookingCalendar', {
      events: calendarEvents,
      selectedDate: selectedDate,
      currentView: currentView,
      onEventClick: handleEventClick,
      onTitleClick: handleTitleClick,
      onDatesSet: handleDatesSet,
      eventContent: renderEventContent,
    });
  };

  // Handle date selection
  const handleDateClick = date => {
    setSelectedDate(date);
  };

  // Reset form
  const resetForm = () => {
    const todayValue = getTodayInputValue();
    setSelectedFacilityType('');
    setOtherFacilityType('');
    setSelectedAmenity('');
    setPurposeOfBooking('');
    setBookingDates([todayValue]);
    setBookingDateInput(todayValue);
    setBookingStartTime('09:00');
    setBookingEndTime('10:00');
    setTimeError('');
    setResidentName('');
    setWhosBooking('');
    setBlockNumber('');
    setApartmentFlatNo('');
    setContactNumber('');
    setEmail('');
    setNumberOfPeople('');
    setBookingNotes('');
    // Don't reset site selection - keep it for convenience
  };

  // Calculate duration in hours from start and end time
  const calculateDurationHours = (startTime, endTime) => {
    const startMinutes = timeToMinutes(startTime);
    const endMinutes = timeToMinutes(endTime);
    let duration = endMinutes - startMinutes;
    if (duration < 0) {
      // Overnight booking
      duration = (24 * 60) - startMinutes + endMinutes;
    }
    return Math.ceil(duration / 60);
  };

  // Handle form submit
  const handleFormSubmit = async e => {
    e.preventDefault();
    
    // Validate required fields
    if (!selectedSite) {
      alert('Please select a site');
      return;
    }
    
    // Check if we have amenities - if yes, require amenity selection; otherwise require facility type
    const hasAmenities = amenityOptions.length > 0;
    
    if (hasAmenities && !selectedAmenity) {
      alert('Please select an amenity');
      return;
    }
    
    if (!hasAmenities && !selectedFacilityType) {
      alert('Please select a facility type');
      return;
    }
    
    // Validate time before submitting
    const timeValidationError = validateTime(bookingStartTime, bookingEndTime);
    if (timeValidationError) {
      setTimeError(timeValidationError);
      return;
    }
    
    // Validate dates
    if (!bookingDates || bookingDates.length === 0) {
      alert('Please select at least one date');
      return;
    }
    
    if (!apartmentFlatNo && !blockNumber) {
      alert('Please enter flat/unit number or block number');
      return;
    }
    
    setIsSubmitting(true);

    // Get facility type - from amenity or manual selection
    const selectedAmenityDetails = getSelectedAmenityDetails();
    const facilityType = hasAmenities 
      ? (selectedAmenityDetails?.label || 'Facility')
      : (selectedFacilityType === 'Other' ? otherFacilityType : selectedFacilityType);

    // Derive start/end dates from the selected dates list
    const sortedDates = [...bookingDates].sort();
    const bookingStartDate = sortedDates[0];
    const bookingEndDate = sortedDates[sortedDates.length - 1];

    // Convert time to ISO format for a given date
    const toTimeISO = (dateStr) => ({
      start: `${dateStr}T${bookingStartTime}:00`,
      end: `${dateStr}T${bookingEndTime}:00`,
    });

    // Frontend payload for passing to invoice (keep the original field names for invoice)
    const frontendPayload = {
      facilityType,
      amenityId: selectedAmenity,
      amenityDetails: selectedAmenityDetails,
      purposeOfBooking,
      dates: sortedDates,
      startDate: bookingStartDate,
      endDate: bookingEndDate,
      startTime: bookingStartTime,
      endTime: bookingEndTime,
      residentName,
      whosBooking,
      blockNumber,
      apartmentFlatNo,
      contactNumber,
      email,
      numberOfPeople,
      notes: bookingNotes,
      siteId: selectedSite,
    };

    setIsSubmitting(false);

    // Navigate to invoice with booking data only; booking will be created when user clicks Create Invoice
    navigate('/managing-facility-booking-invoice', {
      state: {
        bookingData: {
          ...frontendPayload,
        },
      },
    });
  };

  // Get month name
  const getMonthName = () => {
    return currentMonth.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
    });
  };

  // Convert bookings to FullCalendar events
  const getCalendarEvents = () => {
    console.log('getCalendarEvents called');
    console.log('API bookings:', bookings);
    console.log('Local bookings:', localBookings);
    console.log('Facility invoices:', facilityInvoices);
    
    // Create a map of booking IDs to their invoices for quick lookup
    const invoicesByBookingId = {};
    facilityInvoices.forEach(invoice => {
      const bookingId = invoice.booking_id || invoice.bookingId;
      if (bookingId) {
        if (!invoicesByBookingId[bookingId]) {
          invoicesByBookingId[bookingId] = [];
        }
        invoicesByBookingId[bookingId].push(invoice);
      }
    });

    // Combine API bookings with local bookings (for when API fails)
    const allBookings = [...bookings];
    
    // Add local bookings that aren't already in the API response
    const apiBookingIds = new Set(bookings.map(b => b.id || b.booking_id));
    localBookings.forEach(localBooking => {
      const localId = localBooking.id || localBooking.booking_id;
      if (!apiBookingIds.has(localId)) {
        allBookings.push(localBooking);
      }
    });
    
    console.log('All bookings combined:', allBookings);

    // Map bookings to calendar events
    const bookingEvents = allBookings.map(booking => {
      const bookingId = booking.id || booking.booking_id;
      const bookingInvoices = invoicesByBookingId[bookingId] || [];
      const hasInvoice = bookingInvoices.length > 0 || booking.invoiceNumber || booking.invoice_number || booking.invoiceId || booking.invoice_id;
      const facilityType = booking.facilityType || booking.facility_type || 'Booking';
      
      // Get invoice info from either the fetched invoices or the booking itself
      const invoiceInfo = bookingInvoices[0] || {};
      const invoiceNumber = invoiceInfo.invoice_number || invoiceInfo.invoiceNumber || booking.invoiceNumber || booking.invoice_number;
      const invoiceId = invoiceInfo.invoice_id || invoiceInfo.invoiceId || booking.invoiceId || booking.invoice_id;
      const invoiceStatus = invoiceInfo.status || invoiceInfo.invoiceStatus || booking.invoiceStatus || booking.invoice_status;
      
      // Handle start_time/end_time which may be ISO timestamps (e.g., "2026-01-21T09:00:00Z")
      // or just time strings (e.g., "09:00")
      const rawStartTime = booking.start_time || booking.startTime;
      const rawEndTime = booking.end_time || booking.endTime;
      const startDate = booking.start_date || booking.startDate || booking.booking_date;
      const endDate = booking.end_date || booking.endDate || booking.booking_date;
      
      // Helper to extract time from ISO string or return as-is
      const extractLocalTime = (timeStr, dateStr) => {
        if (!timeStr) return dateStr ? `${dateStr}T00:00` : null;
        
        // If it's an ISO string with T and possibly Z
        if (timeStr.includes('T')) {
          // Remove Z suffix if present to treat as local time
          const withoutZ = timeStr.replace('Z', '');
          return withoutZ;
        }
        
        // It's just a time string like "09:00"
        return dateStr ? `${dateStr}T${timeStr}` : timeStr;
      };
      
      const eventStart = extractLocalTime(rawStartTime, startDate);
      const eventEnd = extractLocalTime(rawEndTime, endDate);
      
      // Get booking status for color coding
      const bookingStatus = booking.status || booking.booking_status || 'pending';
      
      return {
        id: bookingId,
        title: hasInvoice ? `${facilityType} (Invoice)` : facilityType,
        start: eventStart,
        end: eventEnd,
        backgroundColor: hasInvoice ? '#10B981' : '#3B82F6', // Blue for bookings, Green for invoiced
        borderColor: hasInvoice ? '#059669' : '#2563EB', // Darker blue border
        extendedProps: {
          eventType: 'booking',
          residentName: booking.residentName || booking.resident_name,
          blockNumber: booking.blockNumber || booking.block_number,
          apartmentFlatNo: booking.apartmentFlatNo || booking.apartment_flat_no,
          contactNumber: booking.contactNumber || booking.contact_number,
          email: booking.email || booking.email,
          purposeOfBooking: booking.purposeOfBooking || booking.purpose_of_booking,
          whosBooking: booking.whosBooking || booking.whos_booking,
          numberOfPeople: booking.numberOfPeople || booking.number_of_people,
          notes: booking.notes || booking.booking_notes,
          bookingStatus: bookingStatus,
          invoiceNumber: invoiceNumber,
          invoiceId: invoiceId,
          invoiceStatus: invoiceStatus,
          hasInvoice: !!hasInvoice,
          invoiceCount: bookingInvoices.length,
        },
      };
    });

    // Map facility invoices to calendar events
    const bookingIds = new Set(bookings.map(b => b.id || b.booking_id));
    const invoiceEvents = facilityInvoices
      .filter(invoice => {
        const bookingId = invoice.booking_id || invoice.bookingId;
        // Only include invoices not already shown via bookings
        return !bookingId || !bookingIds.has(bookingId);
      })
      .map(invoice => {
        const invoiceDate = invoice.invoice_date || invoice.invoiceDate || invoice.date;
        const dueDate = invoice.due_date || invoice.dueDate;
        const facilityType = invoice.facility_type || invoice.facilityType || 'Facility';
        
        return {
          id: `invoice-${invoice.invoice_id || invoice.id}`,
          title: `${facilityType} - Invoice #${invoice.invoice_number || invoice.invoiceNumber || 'N/A'}`,
          start: invoiceDate,
          end: dueDate || invoiceDate,
          backgroundColor: '#10B981', // Green for facility invoices
          borderColor: '#059669',
          extendedProps: {
            eventType: 'invoice',
            facilityType: facilityType,
            invoiceNumber: invoice.invoice_number || invoice.invoiceNumber,
            invoiceId: invoice.invoice_id || invoice.id,
            invoiceStatus: invoice.status || invoice.payment_status,
            totalAmount: invoice.total || invoice.total_amount,
            customerName: invoice.customer_name || invoice.customerName,
            bookingId: invoice.booking_id || invoice.bookingId,
            hasInvoice: true,
          },
        };
      });

    const allEvents = [...bookingEvents, ...invoiceEvents];
    console.log('Calendar events generated:', allEvents);
    return allEvents;
  };

  // Get event color based on facility type
  const getEventColor = facilityType => {
    const colors = {
      'Mini Theater': '#4CAF50',
      Billiards: '#2196F3',
      Hall: '#FF9800',
      'Badminton Court': '#9C27B0',
      Other: '#607D8B',
    };
    return colors[facilityType] || '#3B82F6';
  };

  // Get booking status color
  const getBookingStatusColor = (status, facilityType) => {
    switch (status) {
      case 'approved':
        return '#10B981'; // Green
      case 'pending':
        return '#F59E0B'; // Amber/Yellow
      case 'rejected':
        return '#EF4444'; // Red
      case 'cancelled':
        return '#6B7280'; // Gray
      default:
        return getEventColor(facilityType);
    }
  };

  const calendarDays = getCalendarDays();
  const calendarEvents = getCalendarEvents();

  // Handle Generate Bill button click from calendar event (inline or full-screen overlay)
  useEffect(() => {
    const handleGenerateBill = (e) => {
      const { eventProps, eventTitle, eventStart, eventEnd } = e.detail;

      // Close full-screen calendar overlay if open so the bill screen appears immediately
      closeOverlay('facilityBookingCalendar');

      // Navigate to Facility Booking Bill with booking data
      navigate('/managing-facility-booking-bill', {
        state: {
          bookingData: {
            bookingId: e.detail.eventId,
            facilityType: eventTitle?.replace(' (Invoice)', '') || eventProps?.facilityType,
            residentName: eventProps?.residentName,
            blockNumber: eventProps?.blockNumber,
            apartmentFlatNo: eventProps?.apartmentFlatNo,
            contactNumber: eventProps?.contactNumber,
            email: eventProps?.email,
            purposeOfBooking: eventProps?.purposeOfBooking,
            whosBooking: eventProps?.whosBooking,
            numberOfPeople: eventProps?.numberOfPeople,
            notes: eventProps?.notes,
            startDate: eventStart ? new Date(eventStart).toISOString().split('T')[0] : '',
            endDate: eventEnd ? new Date(eventEnd).toISOString().split('T')[0] : '',
            startTime: eventStart ? new Date(eventStart).toTimeString().slice(0, 5) : '',
            endTime: eventEnd ? new Date(eventEnd).toTimeString().slice(0, 5) : '',
            siteId: selectedSite,
          },
        },
      });
    };

    window.addEventListener('generateBill', handleGenerateBill);
    return () => window.removeEventListener('generateBill', handleGenerateBill);
  }, [navigate, selectedSite, closeOverlay]);

  // Handle event click on calendar
  const handleEventClick = (info) => {
    const props = info.event.extendedProps;
    
    // Check if it's an invoice event
    if (props.eventType === 'invoice') {
      const bookingInfo = props.bookingId 
        ? `\nBooking ID: ${props.bookingId}` 
        : '';
      alert(
        `Facility Invoice Details:\n` +
          `Facility: ${props.facilityType || 'N/A'}\n` +
          `Invoice #: ${props.invoiceNumber || 'N/A'}\n` +
          `Status: ${props.invoiceStatus || 'N/A'}\n` +
          `Customer: ${props.customerName || 'N/A'}\n` +
          `Total Amount: ${props.totalAmount ? `₹${props.totalAmount}` : 'N/A'}` +
          bookingInfo
      );
    } else {
      // It's a booking event - show details with option to generate bill
      const invoiceInfo = props.hasInvoice 
        ? `\n\n--- Invoice Info ---\nInvoice #: ${props.invoiceNumber || 'N/A'}\nStatus: ${props.invoiceStatus || 'Created'}${props.invoiceCount > 1 ? `\nTotal Invoices: ${props.invoiceCount}` : ''}`
        : '\n\nClick "Generate bill" button on the event to create an invoice.';
      alert(
        `Booking Details:\n` +
          `Facility: ${info.event.title}\n` +
          `Resident: ${props.residentName || 'N/A'}\n` +
          `Block: ${props.blockNumber || 'N/A'}\n` +
          `Flat: ${props.apartmentFlatNo || 'N/A'}\n` +
          `Contact: ${props.contactNumber || 'N/A'}\n` +
          `Purpose: ${props.purposeOfBooking || 'N/A'}\n` +
          `Status: ${props.bookingStatus || 'N/A'}` +
          invoiceInfo
      );
    }
  };

  return (
    <div
      className={`flex w-full gap-2 ${
        isMobileViewport ? 'flex-col min-h-screen' : 'flex-row h-screen'
      }`}
    >
      {/* Left Sidebar - Booking Form */}
      <div
        className={`${
          isMobileViewport ? 'w-full' : 'w-96'
        } bg-white/10 backdrop-blur-md border border-white/10 rounded-xl shadow-lg p-4 flex flex-col overflow-y-auto hide-scrollbar`}
        style={
          isMobileViewport
            ? {
                minHeight: '400px',
              }
            : undefined
        }
      >
        {/* Title */}
        <h2 className={`${typography.h2} text-gray-900 mb-4`}>Amenity Booking</h2>

        {/* Booking Form */}
        <form className="flex flex-col gap-4 flex-1" onSubmit={handleFormSubmit}>
          {/* Site Selection Dropdown */}
          <div className="flex flex-col gap-1">
            <label
              className="text-sm font-semibold text-green-800"
              htmlFor="site-selection"
            >
              Site <span className="text-red-500">*</span>
            </label>
            <div className="relative" ref={siteDropdownRef}>
              <button
                type="button"
                onClick={() => setIsSiteDropdownOpen(!isSiteDropdownOpen)}
                disabled={isSitesLoading}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white/80 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 flex items-center justify-between disabled:opacity-50"
              >
                <span className={selectedSite ? 'text-gray-900' : 'text-gray-400'}>
                  {isSitesLoading ? 'Loading sites...' : getSelectedSiteLabel()}
                </span>
                <ChevronDown
                  className={`w-4 h-4 text-gray-500 transition-transform ${
                    isSiteDropdownOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {isSiteDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden max-h-48 overflow-y-auto">
                  {siteOptions.length > 0 ? (
                    siteOptions.map((site) => (
                      <button
                        key={site.value}
                        type="button"
                        onClick={() => {
                          setSelectedSite(site.value);
                          setIsSiteDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors text-sm border-b border-gray-100 last:border-b-0 ${
                          selectedSite === site.value ? 'bg-green-50 text-green-700' : 'text-gray-900'
                        }`}
                      >
                        {site.label}
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-2 text-sm text-gray-500">
                      No sites available
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Amenity/Facility Selection - Show amenities if available, otherwise show facility types */}
          {amenityOptions.length > 0 ? (
            /* Amenity Dropdown - When amenities are available for the site */
            <div className="flex flex-col gap-1">
              <label
                className="text-sm font-semibold text-green-800"
                htmlFor="amenity-selection"
              >
                Amenity <span className="text-red-500">*</span>
              </label>
              <div className="relative" ref={amenityDropdownRef}>
                <button
                  type="button"
                  onClick={() => setIsAmenityDropdownOpen(!isAmenityDropdownOpen)}
                  disabled={isAmenitiesLoading}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white/80 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 flex items-center justify-between disabled:opacity-50"
                >
                  <span className={selectedAmenity ? 'text-gray-900' : 'text-gray-400'}>
                    {isAmenitiesLoading ? 'Loading amenities...' : getSelectedAmenityLabel()}
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 text-gray-500 transition-transform ${
                      isAmenityDropdownOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {isAmenityDropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden max-h-48 overflow-y-auto">
                    {amenityOptions.map((amenity) => (
                      <button
                        key={amenity.value}
                        type="button"
                        onClick={() => {
                          setSelectedAmenity(amenity.value);
                          setIsAmenityDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors text-sm border-b border-gray-100 last:border-b-0 ${
                          selectedAmenity === amenity.value ? 'bg-green-50 text-green-700' : 'text-gray-900'
                        }`}
                      >
                        <div className="flex flex-col">
                          <span className="font-medium">{amenity.label}</span>
                          <span className="text-xs text-gray-500">
                            {amenity.type}{amenity.capacity ? ` • Capacity: ${amenity.capacity}` : ''}
                            {amenity.isPaid && amenity.hourlyRate ? ` • ₹${amenity.hourlyRate}/hr` : ''}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {/* Show selected amenity details */}
              {selectedAmenity && getSelectedAmenityDetails() && (
                <div className="mt-1 p-2 bg-green-50 rounded-lg text-xs text-green-700">
                  {getSelectedAmenityDetails().isPaid && getSelectedAmenityDetails().hourlyRate && (
                    <span>Rate: ₹{getSelectedAmenityDetails().hourlyRate}/hour</span>
                  )}
                  {getSelectedAmenityDetails().capacity && (
                    <span className="ml-2">• Max Capacity: {getSelectedAmenityDetails().capacity}</span>
                  )}
                </div>
              )}
            </div>
          ) : (
            /* Facility Type Dropdown - Fallback when no amenities configured */
            <div className="flex flex-col gap-1">
              <label
                className="text-sm font-semibold text-green-800"
                htmlFor="facility-type"
              >
                Facility Type <span className="text-red-500">*</span>
              </label>
              {isAmenitiesLoading ? (
                <div className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white/80 text-sm text-gray-400">
                  Loading amenities...
                </div>
              ) : (
                <>
                  <div className="relative" ref={facilityDropdownRef}>
                    <button
                      type="button"
                      onClick={() => setIsFacilityDropdownOpen(!isFacilityDropdownOpen)}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white/80 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 flex items-center justify-between"
                    >
                      <span className={selectedFacilityType ? 'text-gray-900' : 'text-gray-400'}>
                        {selectedFacilityType || 'Select Facility Type'}
                      </span>
                      <ChevronDown
                        className={`w-4 h-4 text-gray-500 transition-transform ${
                          isFacilityDropdownOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </button>

                    {isFacilityDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden">
                        {facilityTypes.map((type, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => {
                              setSelectedFacilityType(type);
                              setIsFacilityDropdownOpen(false);
                            }}
                            className="w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors text-gray-900 text-sm border-b border-gray-100 last:border-b-0"
                          >
                            {type}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Other Facility Type Input */}
          {!amenityOptions.length && selectedFacilityType === 'Other' && (
            <div className="flex flex-col gap-1">
              <label
                className="text-sm font-semibold text-green-800"
                htmlFor="other-facility-type"
              >
                Enter other type
              </label>
              <input
                id="other-facility-type"
                type="text"
                value={otherFacilityType}
                onChange={e => setOtherFacilityType(e.target.value)}
                placeholder="Enter other type"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white/80 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
          )}

          {/* Purpose of Booking */}
          <div className="flex flex-col gap-1">
            <label
              className="text-sm font-semibold text-green-800"
              htmlFor="purpose-of-booking"
            >
              Purpose of Booking
            </label>
            <input
              id="purpose-of-booking"
              type="text"
              value={purposeOfBooking}
              onChange={e => setPurposeOfBooking(e.target.value)}
              placeholder="Enter purpose of booking"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white/80 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          {/* Set Date & Time Section */}
          <div className="flex flex-col gap-2">
            <span className="text-sm font-semibold text-green-800">Set Date & Time</span>

            {/* Date selection with Add button */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <div className="flex-1 relative">
                  <input
                    type="date"
                    value={bookingDateInput}
                    min={getTodayInputValue()}
                    onChange={e => setBookingDateInput(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white/80 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const value = bookingDateInput;
                    const todayStr = getTodayInputValue();
                    if (!value) return;
                    // Prevent adding past dates
                    if (value < todayStr) return;
                    setBookingDates(prev =>
                      prev.includes(value) ? prev : [...prev, value]
                    );
                  }}
                  className="px-3 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-medium"
                >
                  Add
                </button>
              </div>

              {/* Selected dates list as tags */}
              {bookingDates && bookingDates.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {bookingDates
                    .slice()
                    .sort()
                    .map(date => (
                      <div
                        key={date}
                        className="flex items-center gap-1 px-2 py-1 rounded-full bg-green-50 border border-green-200 text-xs text-green-800"
                      >
                        <span>{date}</span>
                        <button
                          type="button"
                          onClick={() =>
                            setBookingDates(prev =>
                              prev.filter(d => d !== date)
                            )
                          }
                          className="w-4 h-4 flex items-center justify-center rounded-full bg-green-200 text-[10px] leading-none hover:bg-green-300"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                </div>
              )}
            </div>

            {/* Time range */}
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <div className="flex-1 flex flex-col">
                  <label className="text-xs text-gray-500 mb-1">Start Time</label>
                  <input
                    type="time"
                    value={bookingStartTime}
                    onChange={e => handleStartTimeChange(e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg border ${timeError ? 'border-red-300' : 'border-gray-200'} bg-white/80 text-sm focus:outline-none focus:ring-2 ${timeError ? 'focus:ring-red-500' : 'focus:ring-green-500'}`}
                  />
                </div>
                <span className="text-sm text-gray-700 mt-4">to</span>
                <div className="flex-1 flex flex-col">
                  <label className="text-xs text-gray-500 mb-1">End Time</label>
                  <input
                    type="time"
                    value={bookingEndTime}
                    onChange={e => handleEndTimeChange(e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg border ${timeError ? 'border-red-300' : 'border-gray-200'} bg-white/80 text-sm focus:outline-none focus:ring-2 ${timeError ? 'focus:ring-red-500' : 'focus:ring-green-500'}`}
                  />
                </div>
              </div>
              
              {/* Time validation error */}
              {timeError && (
                <p className="text-xs text-red-500 mt-1">{timeError}</p>
              )}
            </div>
          </div>

          {/* Info Section */}
          <div className="flex flex-col gap-3">
            <span className="text-sm font-semibold text-green-800">Info</span>

            {/* Who's Booking */}
            <div className="flex flex-col gap-1">
              <label
                className="text-xs font-medium text-gray-600"
                htmlFor="whos-booking"
              >
                Who's Booking
              </label>
              <select
                id="whos-booking"
                value={whosBooking}
                onChange={e => setWhosBooking(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white/80 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="" disabled>Select who's booking</option>
                <option value="owner">Owner</option>
                <option value="resident">Resident</option>
                <option value="community">Community</option>
              </select>
            </div>

            {/* Name */}
            <div className="flex flex-col gap-1">
              <label
                className="text-xs font-medium text-gray-600"
                htmlFor="resident-name"
              >
                Name
              </label>
              <input
                id="resident-name"
                type="text"
                value={residentName}
                onChange={e => setResidentName(e.target.value)}
                placeholder="Enter Name"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white/80 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            {/* Block Number */}
            <div className="flex flex-col gap-1">
              <label
                className="text-xs font-medium text-gray-600"
                htmlFor="block-number"
              >
                Block Number
              </label>
              <input
                id="block-number"
                type="text"
                value={blockNumber}
                onChange={e => setBlockNumber(e.target.value)}
                placeholder="enter Cargo capacity"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white/80 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            {/* Apartment / Flat No */}
            <div className="flex flex-col gap-1">
              <label
                className="text-xs font-medium text-gray-600"
                htmlFor="apartment-flat-no"
              >
                Apartment / Flat No
              </label>
              <input
                id="apartment-flat-no"
                type="text"
                value={apartmentFlatNo}
                onChange={e => setApartmentFlatNo(e.target.value)}
                placeholder="Enter vehicle color"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white/80 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            {/* Contact Number */}
            <div className="flex flex-col gap-1">
              <label
                className="text-xs font-medium text-gray-600"
                htmlFor="contact-number"
              >
                Contact Number
              </label>
              <input
                id="contact-number"
                type="tel"
                value={contactNumber}
                onChange={e => setContactNumber(e.target.value)}
                placeholder="Enter contact number"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white/80 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            {/* Email */}
            <div className="flex flex-col gap-1">
              <label
                className="text-xs font-medium text-gray-600"
                htmlFor="email"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Enter email address"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white/80 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>

          {/* Additional Details (Optional) */}
          <div className="flex flex-col gap-3">
            <span className="text-sm font-semibold text-green-800">Additional Details (Optional)</span>

            {/* Number of People */}
            <div className="flex flex-col gap-1">
              <label
                className="text-xs font-medium text-gray-600"
                htmlFor="number-of-people"
              >
                Number of People
              </label>
              <input
                id="number-of-people"
                type="number"
                min="1"
                value={numberOfPeople}
                onChange={e => setNumberOfPeople(e.target.value)}
                placeholder="Enter number of people"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white/80 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            {/* Notes */}
            <div className="flex flex-col gap-1">
              <label
                className="text-xs font-medium text-gray-600"
                htmlFor="booking-notes"
              >
                Notes
              </label>
              <textarea
                id="booking-notes"
                value={bookingNotes}
                onChange={e => setBookingNotes(e.target.value)}
                placeholder="Any additional notes..."
                rows={2}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white/80 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-auto pt-4">
            <PrimaryButton
              type="submit"
              disabled={
                isSubmitting || 
                !selectedSite || 
                (amenityOptions.length > 0 ? !selectedAmenity : !selectedFacilityType) || 
                !residentName || 
                (!apartmentFlatNo && !blockNumber) ||
                !!timeError || 
                bookingDates.length === 0
              }
              className="w-full h-12 text-base font-semibold bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Confirm and Proceed to Invoice'}
            </PrimaryButton>
          </div>
        </form>
      </div>

      {/* Right Main Content - Weekly Calendar */}
      <div
        className={`${
          isMobileViewport ? 'w-full flex-1' : 'flex-1'
        } bg-white/10 backdrop-blur-md border border-white/10 rounded-xl shadow-lg overflow-hidden flex flex-col`}
        style={
          isMobileViewport
            ? {
                minHeight: '500px',
              }
            : undefined
        }
      >
        {/* Calendar Header */}
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Mini Calendar Navigation */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigateMonth('prev')}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-gray-700" />
              </button>
              <span className="text-sm font-medium text-gray-700 min-w-[120px] text-center">
                {getMonthName()}
              </span>
              <button
                onClick={() => navigateMonth('next')}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-gray-700" />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">GMT+05:30</span>
            <button
              onClick={handleViewCalendarFullScreen}
              className="px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 transition-colors flex items-center gap-2 text-sm font-semibold text-white shadow-sm"
              title="View Calendar in Full Screen"
            >
              <Maximize2 className="w-4 h-4" />
              View Calendar
            </button>
            <button
              onClick={() => fetchBookings()}
              disabled={isLoading}
              className="p-2 rounded-lg hover:bg-white/20 transition-colors disabled:opacity-50"
              title="Refresh bookings"
            >
              <RefreshCw
                className={`w-4 h-4 text-gray-600 ${isLoading ? 'animate-spin' : ''}`}
              />
            </button>
          </div>
        </div>

        {/* Calendar Component */}
        <div className="flex-1 overflow-auto relative">
          <Calendar
            ref={calendarRef}
            selectedDate={selectedDate}
            events={calendarEvents}
            eventContent={renderEventContent}
            slotMinTime="06:00:00"
            slotMaxTime="22:00:00"
            allDaySlot={false}
            nowIndicator={true}
            height="100%"
            initialView={currentView}
            onEventClick={handleEventClick}
            onTitleClick={handleTitleClick}
            onDatesSet={handleDatesSet}
          />
          
          {/* Dynamic Picker Dropdown based on view */}
          {isMonthYearPickerOpen && (
            <div
              ref={monthYearPickerRef}
              className="absolute top-16 left-1/2 transform -translate-x-1/2 z-50 bg-white rounded-lg shadow-xl border border-gray-200 p-4 min-w-[280px] max-w-[400px]"
            >
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  {currentView === 'dayGridMonth' && 'Select Month & Year'}
                  {currentView === 'timeGridWeek' && 'Select Week & Year'}
                  {currentView === 'timeGridDay' && 'Select Day, Month & Year'}
                </h3>
                
                {currentView === 'dayGridMonth' && (
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Month</label>
                      <select
                        value={pickerMonth}
                        onChange={(e) => setPickerMonth(parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                      >
                        {Array.from({ length: 12 }, (_, i) => {
                          const monthName = new Date(2024, i).toLocaleDateString('en-US', { month: 'long' });
                          return (
                            <option key={i} value={i}>
                              {monthName}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Year</label>
                      <select
                        value={pickerYear}
                        onChange={(e) => setPickerYear(parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                      >
                        {Array.from({ length: 20 }, (_, i) => {
                          const year = new Date().getFullYear() - 10 + i;
                          return (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>
                )}
                
                {currentView === 'timeGridWeek' && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Year</label>
                      <select
                        value={pickerYear}
                        onChange={(e) => {
                          const newYear = parseInt(e.target.value);
                          setPickerYear(newYear);
                          // Reset week to first week of new year
                          setPickerWeek(1);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                      >
                        {Array.from({ length: 20 }, (_, i) => {
                          const year = new Date().getFullYear() - 10 + i;
                          return (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Week</label>
                      <select
                        value={pickerWeek}
                        onChange={(e) => setPickerWeek(parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white max-h-48 overflow-y-auto"
                      >
                        {getWeeksForYear(pickerYear).map((week) => (
                          <option key={week.number} value={week.number}>
                            {week.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
                
                {currentView === 'timeGridDay' && (
                  <div className="space-y-3">
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Month</label>
                        <select
                          value={pickerMonth}
                          onChange={(e) => {
                            const newMonth = parseInt(e.target.value);
                            setPickerMonth(newMonth);
                            // Adjust day if it exceeds days in new month
                            const daysInMonth = new Date(pickerYear, newMonth + 1, 0).getDate();
                            if (pickerDay > daysInMonth) {
                              setPickerDay(daysInMonth);
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                        >
                          {Array.from({ length: 12 }, (_, i) => {
                            const monthName = new Date(2024, i).toLocaleDateString('en-US', { month: 'long' });
                            return (
                              <option key={i} value={i}>
                                {monthName}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Year</label>
                        <select
                          value={pickerYear}
                          onChange={(e) => setPickerYear(parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                        >
                          {Array.from({ length: 20 }, (_, i) => {
                            const year = new Date().getFullYear() - 10 + i;
                            return (
                              <option key={year} value={year}>
                                {year}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Day</label>
                      <select
                        value={pickerDay}
                        onChange={(e) => setPickerDay(parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                      >
                        {getDaysForMonth(pickerMonth, pickerYear).map((dayItem) => (
                          <option key={dayItem.day} value={dayItem.day}>
                            {dayItem.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setIsMonthYearPickerOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePickerSelect}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                >
                  Go
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

// Custom event content renderer with Generate Bill button
function renderEventContent(eventInfo) {
  const props = eventInfo.event.extendedProps;
  const isBooking = props.eventType === 'booking' || !props.eventType;
  const hasInvoice = props.hasInvoice;
  
  return (
    <div className="p-1 text-xs overflow-hidden">
      <div className="flex items-center justify-between gap-1">
        <div className="font-semibold truncate flex-1">{eventInfo.event.title}</div>
        {isBooking && !hasInvoice && (
          <button
            className="px-2 py-0.5 bg-green-600 text-white text-[10px] rounded hover:bg-green-700 whitespace-nowrap"
            onClick={(e) => {
              e.stopPropagation();
              // Dispatch custom event to handle navigation
              const event = new CustomEvent('generateBill', { 
                detail: { 
                  eventId: eventInfo.event.id,
                  eventProps: props,
                  eventTitle: eventInfo.event.title,
                  eventStart: eventInfo.event.start,
                  eventEnd: eventInfo.event.end,
                }
              });
              window.dispatchEvent(event);
            }}
          >
            Generate bill
          </button>
        )}
      </div>
      <div className="truncate opacity-80">
        {eventInfo.timeText}
      </div>
    </div>
  );
}

export default FacilityBooking;
